import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, LogIn, X } from 'lucide-react';
import { isRatedClock, type Color, type RatedClock } from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { useDisplayName, type DisplayIdentity } from '../app/identity.ts';
import { usePreferences, useT } from '../app/preferences.ts';
import { VsIntro, type VsPlayer } from '../components/VsIntro.tsx';
import { localizeHref, navigate } from '../app/router.ts';
import { BigTile } from '../components/BigTile.tsx';
import { Link } from '../components/Link.tsx';
import { useMatchmaking } from '../online/useMatchmaking.ts';

/**
 * Online play. Signed-in players get rated games against similar ratings;
 * guests meet other guests. Either way nobody waits more than a few seconds.
 */
export function OnlinePage() {
  const { user, canSignIn, signIn } = useAuth();
  const { prefs } = usePreferences();
  const rated = Boolean(user?.username);
  return (
    <Matchmaking
      rating={rated ? user!.rating : null}
      guestName={prefs.playerName}
      locale={prefs.locale}
      onSignIn={!rated && canSignIn ? () => signIn('/online') : null}
    />
  );
}

function Matchmaking({
  rating,
  guestName,
  locale,
  onSignIn,
}: {
  rating: number | null;
  guestName: string;
  locale: string;
  onSignIn: (() => void) | null;
}) {
  const t = useT();
  const me = useDisplayName();
  const { state, start: startSearch, cancel } = useMatchmaking();
  const start = useCallback((clock: RatedClock) => startSearch(clock, { name: guestName, locale }), [startSearch, guestName, locale]);

  // "Find a new opponent" after a rated game comes back here with ?clock=…
  useEffect(() => {
    const clock = new URLSearchParams(window.location.search).get('clock');
    if (isRatedClock(clock)) {
      window.history.replaceState(null, '', localizeHref('/online'));
      start(clock);
    }
  }, [start]);


  if (state.phase === 'searching') return <Searching clock={state.clock} since={state.since} rated={rating !== null} onCancel={cancel} />;

  if (state.phase === 'matched') {
    return <MatchIntro me={me} myColor={state.color} opponent={state.opponent} code={state.code} />;
  }

  const pick = (clock: RatedClock) => start(clock);

  return (
    <div className="page-fill">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">{t('online.title')}</h1>
        {rating !== null && <span className="badge badge-accent rating-badge">🏆 {t('online.rating', { rating })}</span>}
      </div>
      <div className="lobby-step">
        {rating === null && (
          <div className="banner">
            <span>{t('online.guestNote')}</span>
            {onSignIn && (
              <button type="button" className="btn btn-sm btn-blue" onClick={onSignIn}>
                <LogIn size={18} /> {t('auth.signInShort')}
              </button>
            )}
          </div>
        )}
        {state.phase === 'error' && (
          <div className="banner banner-danger" role="alert">
            {t(state.reason === 'replaced' ? 'online.replaced' : 'online.error')}
          </div>
        )}
        <div className="mode-grid pop-in">
          <BigTile
            kind="computer"
            art="⚡"
            title={t('online.blitz.title')}
            desc={t('online.blitz.desc')}
            go={t('home.go')}
            onClick={() => pick('blitz')}
          />
          <BigTile
            kind="join"
            art="🐢"
            title={t('online.rapid.title')}
            desc={t('online.rapid.desc')}
            go={t('home.go')}
            onClick={() => pick('rapid')}
          />
        </div>
      </div>
    </div>
  );
}

function Searching({ clock, since, rated, onCancel }: { clock: RatedClock; since: number; rated: boolean; onCancel: () => void }) {
  const t = useT();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const seconds = Math.max(0, Math.floor((now - since) / 1000));

  return (
    <div className="card waiting pop-in" role="status">
      <span className="waiting-art emoji searching-art" aria-hidden="true">
        🔍
      </span>
      <div className="lobby-card-head">
        <h1 className="lobby-card-title" style={{ fontSize: 34, justifyContent: 'center' }}>
          {t('online.searching')}
        </h1>
        <p className="lobby-card-desc">{t(rated ? 'online.searchingHint' : 'online.searchingGuest')}</p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <span className="badge">⏱ {t(clock === 'blitz' ? 'online.blitz.desc' : 'online.rapid.desc')}</span>
        <span className="badge badge-accent">{t('online.waited', { seconds })}</span>
      </div>
      <div className="waiting-indicator">
        <span className="spinner" style={{ width: 24, height: 24 }} />
      </div>
      <button type="button" className="btn" onClick={onCancel}>
        <X size={22} /> {t('online.cancel')}
      </button>
    </div>
  );
}

const COUNTDOWN_S = 5;

/** "You vs them" face-off with a 5-second countdown, then into the game. */
function MatchIntro({
  me,
  myColor,
  opponent,
  code,
}: {
  me: DisplayIdentity;
  myColor: Color;
  opponent: VsPlayer;
  code: string;
}) {
  const t = useT();
  const enter = useCallback(() => navigate(`/r/${code}`), [code]);
  return <VsIntro title={t('online.found')} me={me} myColor={myColor} opponent={opponent} seconds={COUNTDOWN_S} onDone={enter} />;
}

